import {
  Card,
  Group,
  NumberInput,
  SegmentedControl,
  Tabs,
  Text,
  Divider,
  Button,
  Switch,
  LoadingOverlay,
} from '@mantine/core'
import { Client } from '@/api/controlUnitApi/client'
import useOtherPanelData from './useOtherPanelData'
import { useOtherPanelState } from './useOtherPanelState'
import { FaSave } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

const zoomLevels = [
  { label: '10', value: String(10) },
  { label: '20', value: String(20) },
  { label: '50', value: String(50) },
  { label: '100', value: String(100) },
  { label: '200', value: String(200) },
]

const apertureLevels = [
  { label: '1', value: String(1) },
  { label: '2', value: String(2) },
  { label: '5', value: String(5) },
  { label: '10', value: String(10) },
  { label: '20', value: String(20) },
]

const infraredLevels = [
  { label: '1', value: String(1) },
  { label: '2', value: String(2) },
  { label: '5', value: String(5) },
  { label: '10', value: String(10) },
  { label: '20', value: String(20) },
]

const OtherPanel = ({
  source,
  client,
  isRefetchingCombinedView,
}: {
  source: string
  client: Client
  isRefetchingCombinedView: boolean
}) => {
  const { t } = useTranslation(['hardware', 'common'])
  const {
    // Zoom data and mutations
    frontLeftZoomData,
    frontRightZoomData,
    frontCenterZoomData,
    mutateFrontLeftZoom,
    mutateFrontRightZoom,
    mutateFrontCenterZoom,
    // Aperture data and mutations
    frontLeftApertureData,
    frontRightApertureData,
    frontCenterApertureData,
    mutateFrontLeftAperture,
    mutateFrontRightAperture,
    mutateFrontCenterAperture,
    // Infrared data and mutations
    frontLeftIrData,
    frontRightIrData,
    mutateFrontLeftIr,
    mutateFrontRightIr,
    // IR-cut filter data and mutations
    frontLeftIrCutFilterData,
    frontRightIrCutFilterData,
    frontCenterIrCutFilterData,
    mutateFrontLeftIrCutFilter,
    mutateFrontRightIrCutFilter,
    mutateFrontCenterIrCutFilter,
    isRefetching,
  } = useOtherPanelData({ client, source, isRefetchingCombinedView })

  const {
    // Increment states
    flIncrement,
    setFlIncrement,
    fcIncrement,
    setFcIncrement,
    frIncrement,
    setFrIncrement,
    flApertureIncrement,
    setFlApertureIncrement,
    fcApertureIncrement,
    setFcApertureIncrement,
    frApertureIncrement,
    setFrApertureIncrement,
    flIrIncrement,
    setFlIrIncrement,
    frIrIncrement,
    setFrIrIncrement,

    // Save button states
    displayFlSaveButton,
    displayFrSaveButton,
    displayFcSaveButton,
    displayFlApertureSaveButton,
    displayFrApertureSaveButton,
    displayFcApertureSaveButton,
    displayFlIrSaveButton,
    displayFrIrSaveButton,

    // Pending values
    pendingFlZoomValue,
    pendingFcZoomValue,
    pendingFrZoomValue,
    pendingFlApertureValue,
    pendingFcApertureValue,
    pendingFrApertureValue,
    pendingFlIrValue,
    pendingFrIrValue,

    // Change handlers
    handleFrontLeftZoomChange,
    handleFrontCenterZoomChange,
    handleFrontRightZoomChange,
    handleFrontLeftApertureChange,
    handleFrontCenterApertureChange,
    handleFrontRightApertureChange,
    handleFrontLeftIrChange,
    handleFrontRightIrChange,

    // Save handlers
    handleFrontLeftZoomSave,
    handleFrontCenterZoomSave,
    handleFrontRightZoomSave,
    handleFrontLeftApertureSave,
    handleFrontCenterApertureSave,
    handleFrontRightApertureSave,
    handleFrontLeftIrSave,
    handleFrontRightIrSave,

    // Set editing states
    setIsEditingFlZoom,
    setIsEditingFcZoom,
    setIsEditingFrZoom,
    setIsEditingFlAperture,
    setIsEditingFcAperture,
    setIsEditingFrAperture,
    setIsEditingFlIr,
    setIsEditingFrIr,
  } = useOtherPanelState({
    frontLeftZoomData,
    frontCenterZoomData,
    frontRightZoomData,
    frontLeftApertureData,
    frontCenterApertureData,
    frontRightApertureData,
    frontLeftIrData,
    frontRightIrData,
    mutateFrontLeftZoom,
    mutateFrontCenterZoom,
    mutateFrontRightZoom,
    mutateFrontLeftAperture,
    mutateFrontCenterAperture,
    mutateFrontRightAperture,
    mutateFrontLeftIr,
    mutateFrontRightIr,
  })

  return (
    <Tabs.Panel value="other">
      <Card shadow="sm" p="lg" radius="md" withBorder mt="lg">
        <LoadingOverlay visible={isRefetching} />
        <Text mb="xs">{t('frontLeftZoom')}</Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={flIncrement.toString()}
            onChange={value => setFlIncrement(parseInt(value))}
            data={zoomLevels}
          />
          <NumberInput
            value={pendingFlZoomValue ?? frontLeftZoomData ?? 0}
            placeholder={t('zoomPlaceholder')}
            clampBehavior="strict"
            min={0}
            max={4073}
            step={flIncrement}
            disabled={frontLeftZoomData === undefined || isRefetching}
            onChange={value => handleFrontLeftZoomChange(Number(value))}
            onInput={() => setIsEditingFlZoom(true)}
            onBlur={() => setIsEditingFlZoom(false)}
          />
          {displayFlSaveButton && (
            <Button ml="auto" leftSection={<FaSave />} onClick={handleFrontLeftZoomSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <Divider />

        <Text mb="xs" mt="md">
          {t('frontCenterZoom')}
        </Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={fcIncrement.toString()}
            onChange={value => setFcIncrement(parseInt(value))}
            data={zoomLevels}
          />
          <NumberInput
            value={pendingFcZoomValue ?? frontCenterZoomData ?? 0}
            placeholder={t('zoomPlaceholder')}
            clampBehavior="strict"
            min={0}
            max={4073}
            step={fcIncrement}
            disabled={frontCenterZoomData === undefined || isRefetching}
            onChange={value => handleFrontCenterZoomChange(Number(value))}
            onInput={() => setIsEditingFcZoom(true)}
            onBlur={() => setIsEditingFcZoom(false)}
          />
          {displayFcSaveButton && (
            <Button ml="auto" leftSection={<FaSave />} onClick={handleFrontCenterZoomSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <Divider />
        <Text mb="xs" mt="md">
          {t('frontRightZoom')}
        </Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={frIncrement.toString()}
            onChange={value => setFrIncrement(parseInt(value))}
            data={zoomLevels}
          />
          <NumberInput
            value={pendingFrZoomValue ?? frontRightZoomData ?? 0}
            placeholder={t('zoomPlaceholder')}
            clampBehavior="strict"
            min={0}
            max={4073}
            step={frIncrement}
            disabled={frontRightZoomData === undefined || isRefetching}
            onChange={value => handleFrontRightZoomChange(Number(value))}
            onInput={() => setIsEditingFrZoom(true)}
            onBlur={() => setIsEditingFrZoom(false)}
          />
          {displayFrSaveButton && (
            <Button ml="auto" leftSection={<FaSave />} onClick={handleFrontRightZoomSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <Divider />

        {/* Aperture Controls */}
        <Text mb="xs" mt="md">
          {t('frontLeftAperture')}
        </Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={flApertureIncrement.toString()}
            onChange={value => setFlApertureIncrement(parseInt(value))}
            data={apertureLevels}
          />
          <NumberInput
            value={pendingFlApertureValue ?? frontLeftApertureData ?? 0}
            placeholder={t('aperturePlaceholder')}
            clampBehavior="strict"
            min={0}
            max={75}
            step={flApertureIncrement}
            disabled={frontLeftApertureData === undefined || isRefetching}
            onChange={value => handleFrontLeftApertureChange(Number(value))}
            onInput={() => setIsEditingFlAperture(true)}
            onBlur={() => setIsEditingFlAperture(false)}
          />
          {displayFlApertureSaveButton && (
            <Button ml="auto" leftSection={<FaSave />} onClick={handleFrontLeftApertureSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <Divider />

        <Text mb="xs" mt="md">
          {t('frontCenterAperture')}
        </Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={fcApertureIncrement.toString()}
            onChange={value => setFcApertureIncrement(parseInt(value))}
            data={apertureLevels}
          />
          <NumberInput
            value={pendingFcApertureValue ?? frontCenterApertureData ?? 0}
            placeholder={t('aperturePlaceholder')}
            clampBehavior="strict"
            min={0}
            max={75}
            step={fcApertureIncrement}
            disabled={frontCenterApertureData === undefined || isRefetching}
            onChange={value => handleFrontCenterApertureChange(Number(value))}
            onInput={() => setIsEditingFcAperture(true)}
            onBlur={() => setIsEditingFcAperture(false)}
          />
          {displayFcApertureSaveButton && (
            <Button ml="auto" leftSection={<FaSave />} onClick={handleFrontCenterApertureSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <Divider />

        <Text mb="xs" mt="md">
          {t('frontRightAperture')}
        </Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={frApertureIncrement.toString()}
            onChange={value => setFrApertureIncrement(parseInt(value))}
            data={apertureLevels}
          />
          <NumberInput
            value={pendingFrApertureValue ?? frontRightApertureData ?? 0}
            placeholder={t('aperturePlaceholder')}
            clampBehavior="strict"
            min={0}
            max={75}
            step={frApertureIncrement}
            disabled={frontRightApertureData === undefined || isRefetching}
            onChange={value => handleFrontRightApertureChange(Number(value))}
            onInput={() => setIsEditingFrAperture(true)}
            onBlur={() => setIsEditingFrAperture(false)}
          />
          {displayFrApertureSaveButton && (
            <Button ml="auto" leftSection={<FaSave />} onClick={handleFrontRightApertureSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <Divider />

        {/* Infrared Controls */}
        <Text mb="xs" mt="md">
          {t('frontLeftInfrared')}
        </Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={flIrIncrement.toString()}
            onChange={value => setFlIrIncrement(parseInt(value))}
            data={infraredLevels}
          />
          <NumberInput
            value={pendingFlIrValue ?? frontLeftIrData ?? 0}
            placeholder={t('infraredPlaceholder')}
            clampBehavior="strict"
            min={0}
            max={255}
            step={flIrIncrement}
            disabled={frontLeftIrData === undefined || isRefetching}
            onChange={value => handleFrontLeftIrChange(Number(value))}
            onInput={() => setIsEditingFlIr(true)}
            onBlur={() => setIsEditingFlIr(false)}
          />
          {displayFlIrSaveButton && (
            <Button ml="auto" leftSection={<FaSave />} onClick={handleFrontLeftIrSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <Divider />

        <Text mb="xs" mt="md">
          {t('frontRightInfrared')}
        </Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={frIrIncrement.toString()}
            onChange={value => setFrIrIncrement(parseInt(value))}
            data={infraredLevels}
          />
          <NumberInput
            value={pendingFrIrValue ?? frontRightIrData ?? 0}
            placeholder={t('infraredPlaceholder')}
            clampBehavior="strict"
            min={0}
            max={255}
            step={frIrIncrement}
            disabled={frontRightIrData === undefined || isRefetching}
            onChange={value => handleFrontRightIrChange(Number(value))}
            onInput={() => setIsEditingFrIr(true)}
            onBlur={() => setIsEditingFrIr(false)}
          />
          {displayFrIrSaveButton && (
            <Button ml="auto" leftSection={<FaSave />} onClick={handleFrontRightIrSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <Divider />
        <Text mb="xs" mt="md">
          {t('irCutFilterEnabled')}
        </Text>
        <Group justify="flex-start" mb="md">
          <Switch
            disabled={frontLeftIrCutFilterData === undefined || isRefetching}
            label={t('frontLeftLabel')}
            checked={frontLeftIrCutFilterData ?? false}
            onChange={value => mutateFrontLeftIrCutFilter(value.currentTarget.checked)}
          />
          <Switch
            label={t('frontRightLabel')}
            checked={frontRightIrCutFilterData ?? false}
            onChange={value => mutateFrontRightIrCutFilter(value.currentTarget.checked)}
            disabled={frontRightIrCutFilterData === undefined || isRefetching}
          />
          <Switch
            label={t('frontCenterLabel')}
            checked={frontCenterIrCutFilterData ?? false}
            disabled={frontCenterIrCutFilterData === undefined || isRefetching}
            onChange={value => mutateFrontCenterIrCutFilter(value.currentTarget.checked)}
          />
        </Group>
      </Card>
    </Tabs.Panel>
  )
}

export default OtherPanel
